/* eslint-disable */
import Link from 'next/link'
import React, { useState } from 'react'

import { useAccountAuthMagicLinkRequestCreate } from '@/shared/api/generated/authentication/authentication'
import { LoginRequest } from '@/shared/api/models'
import { initiateOauthFlow } from '@/shared/auth/socialAuth'
import { useCooldownTimer } from '@/shared/hooks/useCooldownTimer'
import { theme } from '@/shared/styles/themes'
import { faEye } from '@fortawesome/pro-light-svg-icons/faEye'
import { faSquare } from '@fortawesome/pro-light-svg-icons/faSquare'
import { Alert, Grid2 as Grid, InputAdornment, Typography } from '@mui/material'

import { Button } from '../Button/Button'
import { CustomIcon } from '../customIcon/CustomIcon'
import { IconButton } from '../IconButton/IconButton'
import { TextField } from '../TextField/TextField'
import {
  StyledAdditionalGrid, StyledCloseButton, StyledCooldownText, StyledCheckBox, StyledCheckboxIcon, StyledCheckBoxLabel,
  StyledDivider, StyledEyeIcon, StyledForm, StyledInstructionSpan, StyledLink, StyledModalHeader, StyledSocialButton, StyledSuccessMessage
} from './styledAuthenticationModal'

type AuthMode = 'magic-link' | 'password' | 'check-email'

export type SignInContentProps = {
  onClose?: () => void
  onSignIn?: (token: LoginRequest) => void
  loading?: boolean
  errorMessage?: string
}

export const SignInContent: React.FC<SignInContentProps> = ({
  onClose,
  onSignIn,
  loading,
  errorMessage,
}) => {
  const [mode, setMode] = useState<AuthMode>('magic-link')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isPasswordHidden, setPasswordHidden] = useState(true)
  const [stayedSignIn, setStayedSignIn] = useState(false)
  const [magicLinkError, setMagicLinkError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { remainingSeconds, isInCooldown, startCooldown } = useCooldownTimer()
  const requestMagicLink = useAccountAuthMagicLinkRequestCreate()

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStayedSignIn(event.target.checked)
  }

  const handlePasswordSignIn: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()

    if (onSignIn) {
      onSignIn({ email, password })
    }
  }

  const handleSendMagicLink: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()
    setMagicLinkError('')
    setIsSubmitting(true)

    try {
      await requestMagicLink.mutateAsync({
        data: {
          email,
          redirect_url: `${window.location.origin}/auth/verify`,
        },
      })

      setIsSubmitting(false)
      startCooldown()
      setMode('check-email')
    } catch (error: any) {
      setIsSubmitting(false)

      const message = error?.response?.data?.error || 'Failed to send magic link. Please try again.'

      setMagicLinkError(message)
    }
  }

  const handleResend = async () => {
    setMagicLinkError('')
    setIsSubmitting(true)

    try {
      await requestMagicLink.mutateAsync({
        data: {
          email,
          redirect_url: `${window.location.origin}/auth/verify`,
        },
      })

      setIsSubmitting(false)
      startCooldown()
    } catch (error: any) {
      setIsSubmitting(false)

      const message = error?.response?.data?.error || 'Failed to resend magic link.'

      setMagicLinkError(message)
    }
  }

  const handleGoogleLogin = () => {
    try {
      initiateOauthFlow('google', { silent: true })
    } catch (error) {
      console.error('Error initiating Google login:', error)
    }
  }

  const isLoading = loading || requestMagicLink.isPending || isSubmitting
  const displayError = mode === 'password' ? errorMessage : magicLinkError

  const renderGoogleOAuth = () => (
    <>
      <Grid size={12} mt={3}>
        <StyledDivider>
          <span>
            <Typography variant="body1">
              OR
            </Typography>
          </span>
        </StyledDivider>
      </Grid>
      <Grid size={12} mt={2}>
        <StyledSocialButton
          size="large"
          onClick={handleGoogleLogin}
          fullWidth
          outline
          disabled={isLoading}
          startIcon={
            <CustomIcon iconCode={'google'} />
          }
          sx={{
            color: theme.palette.text.secondary,
            borderColor: theme.palette.text.secondary,
          }}
        >
          Sign in with Google
        </StyledSocialButton>
      </Grid>
    </>
  )

  const renderModeToggle = () => {
    if (mode === 'password') {
      return (
        <Grid mt={2} mb={2} size={12}>
          <StyledInstructionSpan>
            <StyledLink
              tabIndex={0}
              onClick={() => { setMode('magic-link'); setMagicLinkError('') }}
            >
              Sign in with magic link instead
            </StyledLink>
          </StyledInstructionSpan>
        </Grid>
      )
    }

    if (mode === 'magic-link') {
      return (
        <Grid mt={2} mb={2} size={12}>
          <StyledInstructionSpan>
            <StyledLink
              tabIndex={0}
              onClick={() => { setMode('password'); setMagicLinkError('') }}
            >
              Sign in with password instead
            </StyledLink>
          </StyledInstructionSpan>
        </Grid>
      )
    }

    return null
  }

  if (mode === 'check-email') {
    return (
      <Grid container>
        <Grid size={12}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid>
              <StyledModalHeader variant="h1">Check your email</StyledModalHeader>
            </Grid>
            <Grid>
              {onClose && <StyledCloseButton outline round disabled={isLoading} onClick={onClose}>
                Close
              </StyledCloseButton>}
            </Grid>
          </Grid>
        </Grid>
        <Grid size={12} mt={2}>
          <StyledSuccessMessage>
            We sent a sign-in link to <strong>{email}</strong>. Click the link in your email to sign in.
          </StyledSuccessMessage>
        </Grid>
        {magicLinkError && (
          <Grid size={12} mt={2}>
            <Alert severity="error" icon={false}>
              {magicLinkError}
            </Alert>
          </Grid>
        )}
        <Grid size={12} mt={2}>
          {isInCooldown ? (
            <StyledCooldownText>
              You can resend in <strong>{remainingSeconds}s</strong>
            </StyledCooldownText>
          ) : (
            <Button
              primary
              size="large"
              fullWidth
              disabled={requestMagicLink.isPending}
              onClick={handleResend}
            >
              Resend magic link
            </Button>
          )}
        </Grid>
        <Grid mt={2} mb={2} size={12}>
          <StyledInstructionSpan>
            <StyledLink
              tabIndex={0}
              onClick={() => { setMode('magic-link'); setMagicLinkError('') }}
            >
              Use a different email
            </StyledLink>
          </StyledInstructionSpan>
        </Grid>
        {renderGoogleOAuth()}
      </Grid>
    )
  }

  if (mode === 'password') {
    return (
      <Grid container>
        <Grid size={12}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid>
              <StyledModalHeader variant="h1">Sign in</StyledModalHeader>
            </Grid>
            <Grid>
              {onClose && <StyledCloseButton outline round disabled={isLoading} onClick={onClose}>
                Close
              </StyledCloseButton>}
            </Grid>
          </Grid>
        </Grid>
        <StyledForm onSubmit={handlePasswordSignIn}>
          {displayError && (
            <Grid size={12}>
              <Alert severity="error" icon={false}>
                {displayError}
              </Alert>
            </Grid>
          )}
          <Grid mt={2} size={12}>
            <TextField
              type="email"
              size={'large'}
              name="email_address"
              label="Email address"
              aria-label="Email"
              value={email}
              onChange={(e) => { setEmail(e.target.value) }}
              required
            />
          </Grid>
          <Grid mt={2} size={12}>
            <TextField
              type={isPasswordHidden ? 'password' : 'text'}
              size={'large'}
              name="password"
              label="Password"
              required
              aria-label="Password"
              onChange={(e) => { setPassword(e.target.value) }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => { setPasswordHidden(old => !old) }}
                      >
                        <StyledEyeIcon icon={faEye} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <StyledAdditionalGrid size={12}>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid>
                <StyledCheckBox
                  checked={stayedSignIn}
                  onChange={handleCheckboxChange}
                  icon={<StyledCheckboxIcon icon={faSquare} />}
                />
                <StyledCheckBoxLabel>Stayed sign in</StyledCheckBoxLabel>
              </Grid>
              <Grid>
                <Link href="/account/password-change">
                  <StyledLink tabIndex={0}>
                    Forgot your password?
                  </StyledLink>
                </Link>
              </Grid>
            </Grid>
          </StyledAdditionalGrid>
          <Grid size={12} mt={2}>
            <Button
              primary
              size={'large'}
              fullWidth
              type="submit"
              disabled={isLoading}
            >
              Sign in
            </Button>
          </Grid>
          {renderGoogleOAuth()}
        </StyledForm>
        {renderModeToggle()}
      </Grid>
    )
  }

  // Default: magic-link mode
  return (
    <Grid container>
      <Grid size={12}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid>
            <StyledModalHeader variant="h1">Sign in</StyledModalHeader>
          </Grid>
          <Grid>
            {onClose && <StyledCloseButton outline round disabled={isLoading} onClick={onClose}>
              Close
            </StyledCloseButton>}
          </Grid>
        </Grid>
      </Grid>
      <StyledForm onSubmit={handleSendMagicLink}>
        {displayError && (
          <Grid size={12}>
            <Alert severity="error" icon={false}>
              {displayError}
            </Alert>
          </Grid>
        )}
        <Grid mt={2} size={12}>
          <TextField
            type="email"
            size={'large'}
            name="email_address"
            label="Email address"
            aria-label="Email"
            value={email}
            onChange={(e) => { setEmail(e.target.value) }}
            required
          />
        </Grid>
        <Grid size={12} mt={2}>
          <Button
            primary
            size={'large'}
            fullWidth
            type="submit"
            disabled={isLoading}
          >
            Send magic link
          </Button>
        </Grid>
        {renderGoogleOAuth()}
      </StyledForm>
      {renderModeToggle()}
    </Grid>
  )
}
